import { Select, Stack, Text, TextInput } from '@mantine/core';
import { DataInputType, DatasetType } from '@/types/automationFields.ts';
import type { DataInput } from '@/types/automationFields.ts';
import type {
  LogicalTarget,
  PublicInterface,
  StructuralPath,
} from '@/types/executionCompositions.ts';
import { pathLabel } from './compositionPresentation.ts';

type Mapping = LogicalTarget['trigger_input_mappings'][number];
/** Values are explicit user choices; compatibility is checked by the server. */
export default function ExplicitMappingEditor({
  inputs,
  mappings,
  sources,
  onChange,
  disabled,
}: {
  inputs: PublicInterface['inputs'];
  mappings: Mapping[];
  sources: { path: StructuralPath; name: string }[];
  onChange: (mappings: Mapping[]) => void;
  disabled: boolean;
}) {
  const choices = [
    ...new Map(
      sources.map((source) => [
        source.path.at(-1)!.step_id,
        { value: source.path.at(-1)!.step_id, label: `${source.name} · ${pathLabel(source.path)}` },
      ]),
    ).values(),
  ];
  const update = (portId: string, data: DataInput) =>
    onChange([
      ...mappings.filter((item) => item.trigger_input_id !== portId),
      { trigger_input_id: portId, data_input: data },
    ]);
  return (
    <Stack>
      {inputs.map((port) => {
        const data = mappings.find((item) => item.trigger_input_id === port.port_id)?.data_input;
        const type = data?.data_input_type ?? DataInputType.Dynamic;
        const config = (data?.config ?? {}) as Record<string, string | null>;
        const setConfig = (next: Record<string, string | null>) =>
          update(port.port_id, {
            name: data?.name ?? null,
            binding_name: data?.binding_name ?? null,
            last_known_display_name: data?.last_known_display_name ?? null,
            data_input_type: type,
            config: next as DataInput['config'],
          });
        return (
          <Stack key={port.port_id} gap="xs">
            <Text size="sm">
              Input {port.port_id} · {port.input_type} · {port.required ? 'required' : 'optional'}
            </Text>
            <Select
              label={`Source kind for ${port.port_id}`}
              value={type}
              disabled={disabled}
              data={[
                DataInputType.Dynamic,
                DataInputType.Static,
                DataInputType.Linked,
                DataInputType.LocalSubflowPort,
                DataInputType.Empty,
              ]}
              onChange={(value) => {
                const nextType = value as DataInputType;
                const nextConfig =
                  nextType === DataInputType.Dynamic
                    ? { source_step_id: null, output_id: null }
                    : nextType === DataInputType.Static
                      ? { static_dataset_id: null }
                      : nextType === DataInputType.Linked
                        ? { linked_dataset_id: null }
                        : nextType === DataInputType.LocalSubflowPort
                          ? { port_id: '' }
                          : { dataset_type: DatasetType.Json };
                update(port.port_id, {
                  name: data?.name ?? null,
                  binding_name: data?.binding_name ?? null,
                  last_known_display_name: data?.last_known_display_name ?? null,
                  data_input_type: nextType,
                  config: nextConfig,
                });
              }}
            />
            {type === DataInputType.Dynamic ? (
              <>
                <Select
                  label={`Source step for ${port.port_id}`}
                  value={config.source_step_id ?? null}
                  data={choices}
                  disabled={disabled}
                  onChange={(value) =>
                    setConfig({ source_step_id: value, output_id: config.output_id ?? null })
                  }
                  searchable
                />
                <TextInput
                  label={`Source output for ${port.port_id}`}
                  value={config.output_id ?? ''}
                  disabled={disabled}
                  onChange={(event) =>
                    setConfig({
                      source_step_id: config.source_step_id ?? null,
                      output_id: event.currentTarget.value || null,
                    })
                  }
                  description="Empty selects the source’s default output."
                />
              </>
            ) : type === DataInputType.Empty ? (
              <Select
                label={`Empty dataset type for ${port.port_id}`}
                data={Object.values(DatasetType)}
                value={config.dataset_type ?? DatasetType.Json}
                disabled={disabled}
                onChange={(value) => setConfig({ dataset_type: value })}
              />
            ) : (
              <TextInput
                label={`${type === DataInputType.Static ? 'Dataset ID' : type === DataInputType.Linked ? 'Linked dataset ID' : 'Module port ID'} for ${port.port_id}`}
                value={
                  config[
                    type === DataInputType.Static
                      ? 'static_dataset_id'
                      : type === DataInputType.Linked
                        ? 'linked_dataset_id'
                        : 'port_id'
                  ] ?? ''
                }
                disabled={disabled}
                onChange={(event) =>
                  setConfig({
                    [type === DataInputType.Static
                      ? 'static_dataset_id'
                      : type === DataInputType.Linked
                        ? 'linked_dataset_id'
                        : 'port_id']: event.currentTarget.value || null,
                  })
                }
              />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}
